/**
 * CompareContainer - Main client component for the /compare/[slug] page
 *
 * This component handles two distinct modes:
 *
 * 1. SSG MODE (isDynamicComparison: false)
 *    - A stored comparison exists in the database
 *    - All data is pre-fetched on the server (categories, related apps, etc.)
 *    - Shows stored TLDR summary and Editor's Verdict content
 *    - Per-criterion summaries prioritize stored DB records (published only)
 *
 * 2. DYNAMIC MODE (isDynamicComparison: true)
 *    - No stored comparison exists - apps are being compared on-the-fly
 *    - Only basic app data is passed from server
 *    - Categories, related apps, trending comparisons fetched client-side
 *    - AI generates TLDR summary and per-criterion summaries
 *
 * Key Features:
 * - App swapping: Users can swap apps in the comparison via the hero menu
 * - App focus: Users can click an app tab to view single-app details
 * - Per-criterion AI summaries: Fetched individually with module-level caching
 * - URL updates: Browser URL updates when apps are swapped (no navigation)
 */
'use client'

import { isDiscontinued } from '@/lib/app-categories/discontinued'
import { useComparisonApps } from '@/hooks/useComparisonApps'
import { useDeferredActivation } from '@/hooks/useDeferredActivation'

import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react'
import type { ReactNode } from 'react'

// Layout & Structure Components
import Meta from '@/components/structure/Meta'
import Container from '@/components/structure/Container'
import TabToggle from '@/components/navigation/Toggles'
import Section from '@/components/content/common/Section'
import Tree from '@/components/content/sidebar/Tree'

// Content Display Components
import RichText from '@/components/content/common/RichText'
// EA-413: Images import kept for rollback — replaced by ScreenshotCarousel
// import Images from '@/components/content/common/Images'
import ScreenshotCarousel, {
  buildScreenshotEntries,
} from '@/components/content/common/ScreenshotCarousel'
import Timestamp from '@/components/ui/Timestamp'
import Authors from '@/components/content/common/Authors'
import Videos from '@/components/content/videos/Videos'
import TextPlaceholder from '@/components/ui/TextPlaceholder'
import PreferredSource from '@/components/ui/PreferredSource'

// Comparison-specific Components
import RelatedComparisons from '@/components/content/common/RelatedComparisons'
import CompareSidebar from '@/components/content/comparisons/compare/CompareSidebar'
import CompareHero from '@/components/content/comparisons/compare/CompareHero'
import CompareCriterias, {
  CriterionOutcome,
  getRatingForCriterion,
} from '@/components/content/comparisons/compare/CompareCriterias'
import CriterionAiSummary from '@/components/content/comparisons/compare/CriterionAiSummary'

// App Display Components
import AppRanking from '@/components/content/app/AppRanking'
import { determinePremiumPartner, pickWeightedPartner } from '@/components/content/app/helpers'
import BestCta from '@/components/content/best/shared/BestCta'
import { fetchTrendingPartnerCandidates } from '@/client-fetch-helpers'
import AppList from '@/components/content/app/AppList'
import AppIcon from '@/components/content/app/AppIcon'

// Shared Components
import Badge from '@/components/ui/Badge'
import ExploreButton from '@/components/content/common/ExploreButton'
import DealsTrending from '@/components/content/deals/root/DealsTrending'
import { Icon } from '@/icons/Icon'
import { PageContextProvider, usePageContext } from '@/components/content/common/PageContext'

// Helpers & Utilities
import { createLogger } from '@/lib/logger'
import { generateIntro } from '@/components/content/comparisons/helpers'
import { generateMenu, MenuItem } from '@/helpers/menus'
import {
  cn,
  getBrandColors,
  toSlug,
  hasRichTextContent,
  scrollToSection,
  isPopulatedObject,
} from '@/lib/utils'
import { queryPayload } from '@/lib/payloadClient'
import { extractUniqueCriteriaFromApp } from '@/lib/formatting'
import { FeaturedPlacement } from '@/lib/analytics'
import {
  orderAppsByRecommendation,
  convertAppToAppCategoryRecommendation,
} from '@/components/content/best/helpers'
import {
  Classification,
  Recommendation,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'

// Client-side Queries (for dynamic mode & app swapping)
import {
  queryRelatedAppsByOverlappingCategoryIds,
  queryTrendingComparisons,
  queryAppsBySlugsWithReviewData,
  queryComparisonByAppSlugs,
  queryComparisonVideoByAppSlugs,
  queryCategoryRankingsByCategoryId,
  queryCategoryRankSnapshotsByCategoryId,
  queryBestCtaByCategoryId,
  type BestCtaData,
} from '@/app/(app)/compare/[slug]/client-fetchers'
import {
  deriveAppRanking,
  resolveAppRankChange,
  resolveRankDeltaOverride,
  type AppRankingSnapshotInput,
} from '@/lib/rankings/appRanking'

// Server Actions (bypass Vercel password protection)
import { generateDynamicComparison, generateVerdictFallback } from '@/lib/actions/comparison-ai'

// Types
import type { CriterionWithRating } from '@/components/ui/CriteriaOverview'
import type { BylineAuthor } from '@/lib/fetch-authors'
import { compareCategoryPriority } from '@/lib/comparison/category-priority'
import type { Block } from '@/types/app'
import type { RankChangesByAcrId } from '@/app/(app)/best/[category]/fetchers'
import type {
  Comparison,
  ComparisonAppsApp,
  ComparisonCategoryCriterion,
  App,
  Criterion,
  Category,
  AppCategoryRecommendation,
  CategoryCriteriaCriterion,
  Review,
  ReviewCategoryCriterionRating,
  Video,
  VideoAppsApp,
} from '@/payload-types'

const logger = createLogger('CompareContainer')

function demoteHeadings(blocks: Block[]): Block[] {
  return blocks.map((block) => {
    if ('tag' in block && typeof block.tag === 'string' && /^h[1-5]$/.test(block.tag)) {
      const level = parseInt(block.tag.charAt(1), 10)
      return { ...block, tag: `h${Math.min(level + 1, 6)}` }
    }
    return block
  })
}

// the following comment was from coderabbitm everything seems to be working and I'm not sure if it's needed:
// In @src/components/content/comparisons/compare/CompareContainer.tsx around lines 116 - 128,
// The DEMOTED_HEADING_STYLES constant currently only preserves visuals for h3 and h4 while
// demoteHeadings handles h1–h5; update DEMOTED_HEADING_STYLES to include selectors for the full demotion range
// (e.g., [&_h2], [&_h3], [&_h4], [&_h5], [&_h6]) so that when demoteHeadings shifts tags
// (function demoteHeadings) the original visual sizes/font weights are preserved for all resulting heading levels;
// ensure each selector maps the demoted tag to the intended text size and weight (add font-bold where appropriate)
// consistent with existing style naming conventions.

// Preserves original visual heading sizes after demotion (h2 styles on h3, h3 styles on h4)
const DEMOTED_HEADING_STYLES =
  // TODO JON - Try uncommenting this and see if it looks better or worse
  // '[&_h2]:text-20 [&_h2]:font-bold md:[&_h2]:text-24 [&_h3]:text-18 [&_h3]:font-bold md:[&_h3]:text-20 [&_h4]:text-18 [&_h4]:font-bold [&_h5]:text-16 [&_h5]:font-bold [&_h6]:text-14 [&_h6]:font-bold'
  '[&_h3]:text-18 [&_h3]:font-bold md:[&_h3]:text-20 [&_h4]:text-18 [&_h4]:font-bold'

const RECOMMENDATION_SCORE: Record<string, number> = {
  [Recommendation.TopPick]: 4,
  [Recommendation.Recommended]: 3,
  [Recommendation.Capable]: 2,
  [Recommendation.NotRecommended]: 1,
}

// Derives overlapping categories directly from app data (no async query needed).
// Mirrors the ranking logic in getOverlappingAppCategories but operates on
// in-memory categoryRecommendations already present on each App object.
function computeOverlappingCategories(apps: App[]): Category[] {
  if (apps.length < 2) return []

  type AppInfo = { classification: string; recommendation: string; discontinued: boolean }
  const categoryMap = new Map<
    Category['id'],
    { category: Category; appInfo: Map<App['id'], AppInfo> }
  >()

  for (const app of apps) {
    const acrs = app.categoryRecommendations?.docs
    if (!acrs) continue
    for (const acr of acrs) {
      if (!acr || typeof acr === 'number') continue
      const cat = (acr as AppCategoryRecommendation).category
      if (!cat || typeof cat === 'number') continue
      const category = cat as Category
      const classification = (acr as AppCategoryRecommendation).classification ?? ''
      const recommendation = (acr as AppCategoryRecommendation).recommendation ?? ''

      if (!categoryMap.has(category.id)) {
        categoryMap.set(category.id, { category, appInfo: new Map() })
      }
      const entry = categoryMap.get(category.id)!
      if (!entry.appInfo.has(app.id)) {
        entry.appInfo.set(app.id, {
          classification,
          recommendation,
          discontinued: isDiscontinued(acr),
        })
      }
    }
  }

  // Keep only categories where ALL apps are present
  const overlapping = Array.from(categoryMap.values()).filter(
    (data) => data.appInfo.size >= apps.length,
  )

  const EFFICIENCY_THRESHOLD = 5
  const scored = overlapping.map((data) => {
    const infos = Array.from(data.appInfo.values()).filter((info) => !info.discontinued)
    const primaryCount = infos.filter((i) => i.classification === Classification.Primary).length
    const bestRec = Math.max(0, ...infos.map((i) => RECOMMENDATION_SCORE[i.recommendation] ?? 0))

    let efficiencyPoints = 0
    for (const app of apps) {
      const acrs = app.categoryRecommendations?.docs
      if (!acrs) continue
      for (const acr of acrs) {
        if (!acr || typeof acr === 'number' || isDiscontinued(acr)) continue
        const cat = (acr as AppCategoryRecommendation).category
        if (!cat || typeof cat === 'number') continue
        if ((cat as Category).id !== data.category.id) continue

        const ccs = (cat as Category).categoryCriteria?.docs as
          | CategoryCriteriaCriterion[]
          | undefined
        if (!ccs) continue

        for (const cc of ccs) {
          const crit = typeof cc.criterion === 'number' ? null : (cc.criterion as Criterion)
          if (!crit?.id) continue
          const rating = getRatingForCriterion(app, crit.id, data.category.id)
          if (rating !== null && rating >= EFFICIENCY_THRESHOLD) {
            efficiencyPoints++
          }
        }
      }
    }

    return {
      category: data.category,
      activeOverlap: infos.length === apps.length,
      primaryCount,
      efficiencyPoints,
      recommendationScore: bestRec,
    }
  })

  // Historical categories still supply comparison content, but cannot displace a current overlap.
  scored.sort(
    (a, b) => Number(b.activeOverlap) - Number(a.activeOverlap) || compareCategoryPriority(a, b),
  )
  return scored.map((s) => s.category)
}

type CompareContainerProps =
  | {
      ssgComparisonApps: App[] | undefined
      ssgCategories: Category[] | undefined
      ssgRelatedApps: App[] | undefined
      ssgComparison: Comparison | null | undefined
      ssgVideo: { video: Video; videoApps: VideoAppsApp[] }[] | undefined
      ssgTrendingComparisons: Comparison[] | undefined
      ssgAlternativeApps: AppCategoryRecommendation[] | undefined
      isDynamicComparison: false
      isSingleAppSelection?: false
      modifiedTimestamp: string | undefined
      authors?: BylineAuthor[]
    }
  | {
      ssgComparisonApps: App[] | undefined
      ssgCategories: undefined
      // Single-app selection mode pre-supplies picker candidates; the 2-app
      // dynamic path leaves this undefined and fetches client-side.
      ssgRelatedApps: App[] | undefined
      ssgComparison: null
      ssgVideo: undefined
      ssgTrendingComparisons: undefined
      ssgAlternativeApps: undefined
      isDynamicComparison: true
      // When true, only one app is selected: render the hero with an empty
      // second box (a picker) and no comparison body.
      isSingleAppSelection?: boolean
      modifiedTimestamp?: undefined
      authors?: BylineAuthor[]
    }

export default function CompareContainer(props: CompareContainerProps) {
  return (
    <PageContextProvider key={props.ssgComparisonApps?.map((app) => app.slug).join('-vs-')}>
      <CompareContainerContent {...props} />
    </PageContextProvider>
  )
}

function getPrimaryReview(app?: App): Review | undefined {
  if (!app?.review?.docs) return undefined

  const reviews = app.review.docs.filter(
    (review): review is Review => typeof review === 'object' && review !== null,
  )

  if (reviews.length === 0) return undefined

  return reviews.find((review) => review?._status === 'published') ?? reviews[0]
}

// Main content component containing all comparison logic and UI.
function CompareContainerContent({
  ssgComparisonApps,
  ssgCategories,
  ssgRelatedApps,
  ssgComparison,
  ssgVideo,
  ssgTrendingComparisons,
  ssgAlternativeApps,
  isDynamicComparison,
  isSingleAppSelection = false,
  modifiedTimestamp,
  authors,
}: CompareContainerProps) {
  // ==========================================================================
  // STATE - Core Data (initialized from SSG props or empty for dynamic mode)
  // ==========================================================================
  const [comparisonApps, setComparisonApps] = useState<App[] | undefined>(ssgComparisonApps)
  const [relatedApps, setRelatedApps] = useState<App[]>(ssgRelatedApps ?? [])
  const [trendingComparisons, setTrendingComparisons] = useState<Comparison[]>(
    ssgTrendingComparisons ?? [],
  )
  const [appsWithReviewData, setAppsWithReviewData] = useState<App[]>(
    isDynamicComparison ? [] : ssgComparisonApps ?? [],
  )
  const [tldr, setTldr] = useState<Record<App['id'], Block[] | null>>({})

  // Full main-list app_category_recommendation set for the primary category, used to
  // show each compared app's true /best page rank instead of its 1/2 position here.
  const [primaryCategoryRankingAcrs, setPrimaryCategoryRankingAcrs] = useState<
    AppCategoryRecommendation[]
  >([])
  // That category's rank-change snapshots, used to derive the same up/down trend
  // arrow the /best page shows alongside the rank number.
  const [primaryCategorySnapshots, setPrimaryCategorySnapshots] = useState<
    AppRankingSnapshotInput[]
  >([])
  // Top apps of each category's /best page, promoted when no compared app is a partner
  const [bestCtaByCategoryId, setBestCtaByCategoryId] = useState<
    Partial<Record<Category['id'], BestCtaData>>
  >({})

  // Tracks the current comparison record (updates when apps are swapped)
  const [currentComparison, setCurrentComparison] = useState<Comparison | null>(
    isDynamicComparison ? null : ssgComparison ?? null,
  )

  // Video state — initialized from SSG prop, updated client-side on app swap or dynamic mount
  const [videoData, setVideoData] = useState<
    { video: Video; videoApps: VideoAppsApp[] }[] | undefined
  >(ssgVideo)

  // ==========================================================================
  // STATE - Loading & AI Summary
  // ==========================================================================
  const [appsWithReviewDataLoaded, setAppsWithReviewDataLoaded] = useState(!isDynamicComparison)
  const [aiGeneratedSummary, setAiGeneratedSummary] = useState<string | null>(null)
  const [aiSummaryLoading, setAiSummaryLoading] = useState(isDynamicComparison)
  const [aiGeneratedVerdict, setAiGeneratedVerdict] = useState<string | null>(null)
  const [aiVerdictLoading, setAiVerdictLoading] = useState(false)
  const [storedComparisonCheckPending, setStoredComparisonCheckPending] = useState(false)
  const [alternativeApps, setAlternativeApps] = useState<AppCategoryRecommendation[]>(
    ssgAlternativeApps ?? [],
  )
  const [trendingApp, setTrendingApp] = useState<AppCategoryRecommendation | undefined>(undefined)
  const [trendingLoaded, setTrendingLoaded] = useState(false)

  // ==========================================================================
  // TODO JON - Skeleton Loaders
  // Use these loading flags to show skeleton loaders in dynamic mode:
  //   - appsWithReviewDataLoaded: Editor's Verdict section
  //   - aiSummaryLoading: Comparison Summary section
  //   - categories: derived via useMemo from apps (always in sync)
  // ==========================================================================

  // ==========================================================================
  // MEMOS - Dependency Tracking
  // ==========================================================================

  const initialAppSlugs = useMemo(
    () => (comparisonApps ?? []).map((app) => app.slug),
    [comparisonApps],
  )

  // ==========================================================================
  // STATE - App Display & Swapping (declared early so categories memo is
  // available for dynamic mode effects below)
  // ==========================================================================

  // Validate that we have app data (should always exist from SSG or dynamic fetch)
  if (!comparisonApps) {
    throw new Error(
      `Comparison apps data is missing: ${JSON.stringify({ comparisonApps }, null, 2)}`,
    )
  }

  // Current apps being displayed (can be modified via app swapping in hero)
  const [apps, setApps] = useComparisonApps(comparisonApps)
  const [appsRefetching, setAppsRefetching] = useState(false)

  // Overlapping categories derived from current apps — recomputes automatically
  // when apps change (no async query needed, data is in app.categoryRecommendations).
  //
  // Three-layer fallback for category stability during swaps:
  //
  // 1. ssgCategories (initial only): Server-fetched categories have full populates
  //    (slug, name, icon). Used to seed prevCategoriesRef on first render so the
  //    ref starts with good data even before any client-side computation.
  //
  // 2. computedCategoriesHaveSlug gate: During a swap, the incoming app from
  //    relatedApps may have category objects WITHOUT slug (if the populate config
  //    was incomplete, or depth was insufficient). Breadcrumbs depend on
  //    primaryCategory.slug — if we accepted these categories, the breadcrumb
  //    would vanish. The slug check rejects incomplete categories.
  //
  // 3. prevCategoriesRef fallback: When computed categories are empty OR lack slug,
  //    we fall back to the last known-good categories. This prevents UI flicker
  //    during the transition window between swap and refetch completing.
  //    The ref is only updated when computed categories pass the slug check.
  const computedCategories = useMemo(() => computeOverlappingCategories(apps), [apps])
  const initialCategories =
    ssgCategories && ssgCategories.length > 0 ? ssgCategories : computedCategories
  const prevCategoriesRef = useRef<Category[]>(initialCategories)
  const computedCategoriesHaveSlug = computedCategories.length > 0 && !!computedCategories[0]?.slug
  const categories = computedCategoriesHaveSlug ? computedCategories : prevCategoriesRef.current
  if (computedCategoriesHaveSlug) {
    prevCategoriesRef.current = computedCategories
  }

  // ==========================================================================
  // EFFECTS - Dynamic Mode Data Fetching
  // These effects only run in dynamic mode to fetch data client-side.
  // In SSG mode, all this data is pre-fetched on the server.
  // ==========================================================================

  // Fetch related apps (depends on categories being loaded)
  useEffect(() => {
    if (!isDynamicComparison || ssgRelatedApps) return
    if (categories.length === 0) return

    let isMounted = true

    async function fetchAndSetRelatedApps() {
      try {
        // Only use the primary category (first in sorted list) so the app
        // selector shows apps directly comparable in the main category
        const primaryCategoryId = categories[0]?.id
        if (!primaryCategoryId) {
          if (isMounted) setRelatedApps([])
          return
        }
        const apps = await queryRelatedAppsByOverlappingCategoryIds([primaryCategoryId])
        if (isMounted) setRelatedApps(apps)
      } catch (error) {
        logger.error('Error fetching related apps:', error)
      }
    }

    fetchAndSetRelatedApps()
    return () => {
      isMounted = false
    }
  }, [isDynamicComparison, ssgRelatedApps, categories])

  // Fetch trending comparisons (independent, can load in parallel)
  useEffect(() => {
    if (!isDynamicComparison || ssgTrendingComparisons) return

    let isMounted = true

    async function fetchAndSetTrendingComparisons() {
      try {
        const appIds = (comparisonApps ?? []).map((app) => app.id)
        if (appIds.length === 0) return
        const comparisons = await queryTrendingComparisons({ appIds })
        if (isMounted) setTrendingComparisons(comparisons)
      } catch (error) {
        logger.error('Error fetching trending comparisons:', error)
      }
    }

    fetchAndSetTrendingComparisons()
    return () => {
      isMounted = false
    }
  }, [isDynamicComparison, ssgTrendingComparisons, comparisonApps])

  // Fetch full app data with review ratings (for Editor's Verdict section)
  useEffect(() => {
    if (!isDynamicComparison) return
    if (initialAppSlugs.length === 0) return

    let isMounted = true

    async function fetchAndSetAppsWithReviewData() {
      try {
        const apps = await queryAppsBySlugsWithReviewData({ slugs: initialAppSlugs })
        if (isMounted) setAppsWithReviewData(apps)
      } catch (error) {
        logger.error('Error fetching apps with review data:', error)
      } finally {
        if (isMounted) setAppsWithReviewDataLoaded(true)
      }
    }

    fetchAndSetAppsWithReviewData()
    return () => {
      isMounted = false
    }
  }, [isDynamicComparison, initialAppSlugs])

  // Fetch video for dynamic mode initial load (ssgVideo is undefined in dynamic mode)
  useEffect(() => {
    if (!isDynamicComparison) return
    if (initialAppSlugs.length === 0) return

    let isMounted = true

    async function fetchAndSetVideo() {
      try {
        const fetchedVideo = await queryComparisonVideoByAppSlugs(initialAppSlugs)
        if (isMounted) setVideoData(fetchedVideo)
      } catch (error) {
        logger.error('Error fetching comparison video:', error)
      }
    }

    fetchAndSetVideo()
    return () => {
      isMounted = false
    }
  }, [isDynamicComparison, initialAppSlugs])

  // Track if apps have been swapped from initial load (for comparison record lookup).
  // currentAppSlugs is a stable string derived from app slugs — used as the primary
  // dependency for swap-related effects instead of the `apps` array reference, because
  // setApps(orderedFullApps) inside the refetch effect changes the reference without
  // changing the slugs, which would cause spurious effect re-runs and self-cancellation.
  const initialAppSlugsRef = useRef(comparisonApps.map((app) => app.slug).join('-vs-'))
  const initialAppSlugsString = initialAppSlugsRef.current
  const currentAppSlugs = useMemo(() => apps.map((app) => app.slug).join('-vs-'), [apps])
  const appsHaveBeenSwapped = currentAppSlugs !== initialAppSlugsString
  // Used by the reset effect to skip initial mount (see comment on that effect)
  const prevAppSlugsRef = useRef(currentAppSlugs)
  const lastTabScrollRef = useRef<string | null>(null)

  // Efficiency points per app for the primary category
  const [appCategoryEfficiencyPoints, setAppCategoryEfficiencyPoints] = useState<
    Record<App['id'], number | undefined>
  >({})
  // Per-app criteria with ratings for the primary category (used by hero popover)
  const [appCategoryCriteriaWithRatings, setAppCategoryCriteriaWithRatings] = useState<
    Record<App['id'], CriterionWithRating[]>
  >({})

  // ==========================================================================
  // MEMOS - Derived Data from Comparison Record
  // ==========================================================================

  const publishedTimestamp = currentComparison?.createdAt

  // Cache key for per-criterion AI summaries (invalidates when apps change)
  const criterionSummaryCacheKey = useMemo(() => {
    const sortedSlugs = [...apps.map((a) => a.slug)].sort().join('-')
    return `criterion-summary-${sortedSlugs}`
  }, [apps])

  // Stored TLDR summary from comparison record (Lexical rich text).
  // Filters out empty paragraph nodes that Lexical initializes by default,
  // so an untouched rich text field correctly reads as "no content."
  const summary = useMemo(() => {
    const children = currentComparison?.tldr?.root?.children
    if (!Array.isArray(children)) return []
    return (children as Block[]).filter(
      (node) =>
        node.type !== 'paragraph' ||
        node.children?.some((child: any) =>
          typeof child.text === 'string' ? child.text.trim().length > 0 : true,
        ),
    )
  }, [currentComparison?.tldr])

  // Stored verdict summary from comparison record (Lexical rich text)
  const verdictSummary = useMemo(() => {
    return Array.isArray(currentComparison?.verdictSummary?.root?.children)
      ? (currentComparison.verdictSummary!.root!.children as Block[])
      : []
  }, [currentComparison?.verdictSummary])

  // Stored per-criterion AI summaries (published only, take priority over AI-generated)
  const storedAiSummaries = useMemo(() => {
    const docs = currentComparison?.aiSummary?.docs as ComparisonCategoryCriterion[] | undefined
    if (!docs) return []
    return docs.filter((doc) => doc?._status === 'published')
  }, [currentComparison?.aiSummary?.docs])

  // ==========================================================================
  // MEMOS - UI Data
  // ==========================================================================

  const primaryCategory = categories[0]
  const appIds = useMemo(() => apps.map((app) => app.id), [apps])
  const { ref: comparisonContentRef, active: rankingFetchActive } = useDeferredActivation({
    strategy: 'after-interaction-idle',
  })

  // Full category ranking + rank-change snapshots for the primary category's
  // AppRanking, so it can show each compared app's true /best page rank (and trend
  // arrow) rather than its 1/2 position within just the compared apps. `categories`
  // here only carries the compared apps' own ACR rows, not the full ranked list for
  // the category, so this is a dedicated client-side fetch.
  useEffect(() => {
    const categoryId = primaryCategory?.id
    // Clear immediately so a category swap never keeps showing the previous
    // category's ranks/trends while the new fetch is in flight — or indefinitely,
    // if it fails.
    setPrimaryCategoryRankingAcrs([])
    setPrimaryCategorySnapshots([])
    if (!categoryId || !rankingFetchActive) return

    let isMounted = true

    async function fetchAndSetCategoryRankings() {
      // Independent settle, not Promise.all: the snapshots query is only for the
      // optional trend arrow, and shouldn't be able to take the rank numbers down
      // with it if it's the one that fails.
      const [acrsResult, snapshotsResult] = await Promise.allSettled([
        queryCategoryRankingsByCategoryId(categoryId),
        queryCategoryRankSnapshotsByCategoryId(categoryId),
      ])
      if (!isMounted) return

      if (acrsResult.status === 'fulfilled') {
        setPrimaryCategoryRankingAcrs(acrsResult.value)
      } else {
        logger.error('Error fetching category rankings:', acrsResult.reason)
      }

      if (snapshotsResult.status === 'fulfilled') {
        setPrimaryCategorySnapshots(snapshotsResult.value)
      } else {
        logger.error('Error fetching category rank snapshots:', snapshotsResult.reason)
      }
    }

    fetchAndSetCategoryRankings()
    return () => {
      isMounted = false
    }
  }, [primaryCategory?.id, rankingFetchActive])

  const hasPartnerApp = apps.some((app) => determinePremiumPartner(app.partnershipTier))
  const getBestCta = useCallback(
    (categoryId: Category['id'] | undefined): BestCtaData | null => {
      if (hasPartnerApp || categoryId === undefined) return null
      return bestCtaByCategoryId[categoryId] ?? null
    },
    [hasPartnerApp, bestCtaByCategoryId],
  )
  const currentBestCta = getBestCta(primaryCategory?.id)
  const categoryIdsKey = categories.map((category) => category.id).join(',')

  useEffect(() => {
    setBestCtaByCategoryId({})
    // Partner comparisons never show the CTA (render is gated too), so skip entirely
    if (hasPartnerApp || !categoryIdsKey || !rankingFetchActive) return

    let isMounted = true
    const categoryIds = categoryIdsKey.split(',').map(Number)

    Promise.all(
      categoryIds.map((categoryId) =>
        queryBestCtaByCategoryId(categoryId).catch((error) => {
          logger.error('Error fetching best CTA apps:', error)
          return null
        }),
      ),
    ).then((results) => {
      if (!isMounted) return
      const byCategoryId: Partial<Record<Category['id'], BestCtaData>> = {}
      results.forEach((data, index) => {
        const categoryId = categoryIds[index]
        if (data && categoryId !== undefined) byCategoryId[categoryId] = data
      })
      setBestCtaByCategoryId(byCategoryId)
    })

    return () => {
      isMounted = false
    }
  }, [categoryIdsKey, hasPartnerApp, rankingFetchActive])

  const rankByAppId = useMemo(() => {
    const { orderedAppRecs: fullCategoryRanking } = orderAppsByRecommendation(
      primaryCategoryRankingAcrs,
      false,
    )
    const map: Partial<Record<App['id'], number>> = {}
    fullCategoryRanking.forEach((rec, index) => {
      if (isPopulatedObject<App>(rec.app)) map[rec.app.id] = index + 1
    })
    return map
  }, [primaryCategoryRankingAcrs])

  // Rank-change trend per compared app's ACR, keyed by ACR id to match AppRanking's
  // `rankChangesByAcrId` prop (same shape the /best page passes). Reuses the same
  // pure derivation (`deriveAppRanking` + `resolveAppRankChange`) as the client-side
  // `getAppRanking` used elsewhere, just batched over the compared apps.
  const rankChangesByAcrId = useMemo(() => {
    const map: RankChangesByAcrId = {}
    if (!primaryCategory) return map

    for (const acr of primaryCategoryRankingAcrs) {
      if (!isPopulatedObject<App>(acr.app) || !appIds.includes(acr.app.id)) continue

      const currentRank = rankByAppId[acr.app.id]
      if (currentRank === undefined) continue

      const snapshotRanking = deriveAppRanking({
        snapshots: primaryCategorySnapshots,
        appId: acr.app.id,
        category: {
          id: primaryCategory.id,
          slug: primaryCategory.slug,
          name: primaryCategory.name,
        },
      })
      if (!snapshotRanking) continue

      if (snapshotRanking.rank !== currentRank) {
        if (acr.displayRankDelta !== false) {
          const override = resolveRankDeltaOverride(acr, currentRank)
          if (override) map[String(acr.id)] = override
        }
        continue
      }

      const rankChange = resolveAppRankChange({
        snapshotRanking,
        acr,
        displayRankDelta: acr.displayRankDelta !== false,
      })
      if (rankChange) map[String(acr.id)] = rankChange
    }

    return map
  }, [primaryCategory, primaryCategoryRankingAcrs, primaryCategorySnapshots, appIds])

  const compareMenu: MenuItem[] = useMemo(
    () =>
      generateMenu('compare', {
        apps: apps,
        relatedApps: relatedApps,
        setApps: setApps,
        options: { isSelectable: true, isShareable: true },
        comparison: apps,
      }),
    [apps, relatedApps],
  )

  const compareSidebarMenu: MenuItem[] = useMemo(
    () =>
      generateMenu('compare', {
        apps: apps,
        relatedApps: relatedApps,
        setApps: setApps,
        options: { isSelectable: true, isShareable: true, isSwappable: true },
        comparison: apps,
      }),
    [apps, relatedApps],
  )

  const compareSidebarMenuByAppId = useMemo(() => {
    const map: Partial<Record<App['id'], MenuItem[]>> = {}
    for (const app of apps) {
      map[app.id] = generateMenu('compare', {
        apps: apps,
        relatedApps: relatedApps,
        setApps: setApps,
        focusApp: app,
        options: { isSelectable: true, isShareable: true, isSwappable: true },
        comparison: apps,
      })
    }
    return map
  }, [apps, relatedApps])

  const { appFocus, setAppFocus, activeSection } = usePageContext()

  // ==========================================================================
  // EFFECTS - App Swapping
  // These effects handle URL updates and data re-fetching when users swap apps.
  // ==========================================================================

  // IMPORTANT: This must be a ref, NOT state. If it were state, calling
  // setLastFetchedSlugs inside the refetch effect would change the effect's deps,
  // triggering cleanup (isMounted = false) before the finally block runs, which
  // permanently leaves appsRefetching = true and efficiency points stuck loading.
  const lastFetchedSlugsRef = useRef(currentAppSlugs)

  useEffect(() => {
    setAppFocus(null)
  }, [apps])

  // Re-fetch complete app data when apps are swapped.
  //
  // CRITICAL DESIGN DECISIONS (do not revert without understanding):
  //
  // 1. Dep is [currentAppSlugs] only — NOT [apps] or [lastFetchedSlugs].
  //    - `apps` in deps would cause self-cancellation: setApps(orderedFullApps)
  //      changes the apps reference → effect re-runs → cleanup sets isMounted=false
  //      → finally block never clears appsRefetching → efficiency points stuck loading.
  //    - `lastFetchedSlugs` as state in deps had the same problem: setLastFetchedSlugs
  //      inside the effect changed the dep → cleanup → isMounted=false.
  //    - currentAppSlugs is a string derived from slugs — same slugs = same string,
  //      so setApps(orderedFullApps) with the same slugs doesn't change it.
  //
  // 2. Loading flags (appsRefetching, appsWithReviewDataLoaded) are cleared in the
  //    try block RIGHT AFTER setting app data, BEFORE the related apps await.
  //    The related apps fetch only affects the swap menu — efficiency points, criteria,
  //    and breadcrumbs all depend on the app data which is already available. Clearing
  //    after the await would leave the UI in loading state during a slow network call.
  //    The finally block acts as a safety net for the error case (apps fetch throws
  //    before reaching the flag-clearing code).
  //
  // 3. Slugs are derived via currentAppSlugs.split('-vs-') to preserve user's visual
  //    order (e.g., notion-vs-motion, not the DB canonical motion-vs-notion).
  useEffect(() => {
    if (currentAppSlugs === lastFetchedSlugsRef.current) return

    let isMounted = true
    setAppsRefetching(true)
    setAppsWithReviewDataLoaded(false)

    async function refetchAppsWithFullData() {
      try {
        const slugs = currentAppSlugs.split('-vs-')
        const fullApps = await queryAppsBySlugsWithReviewData({ slugs })

        if (isMounted) {
          const orderedFullApps = slugs
            .map((slug) => fullApps.find((app) => app.slug === slug))
            .filter((app): app is App => app !== undefined)

          setApps(orderedFullApps)
          setAppsWithReviewData(orderedFullApps)
          lastFetchedSlugsRef.current = currentAppSlugs
          setAppsRefetching(false)
          setAppsWithReviewDataLoaded(true)

          // Related apps + video fetch in parallel (independent, non-blocking)
          const newCategories = computeOverlappingCategories(orderedFullApps)
          const newPrimaryCategoryId = newCategories[0]?.id
          const [newRelatedApps, newVideo] = await Promise.all([
            newPrimaryCategoryId
              ? queryRelatedAppsByOverlappingCategoryIds([newPrimaryCategoryId])
              : Promise.resolve([]),
            queryComparisonVideoByAppSlugs(slugs),
          ])
          if (isMounted) {
            setRelatedApps(newRelatedApps)
            setVideoData(newVideo)
          }
        }
      } catch (error) {
        logger.error('Error re-fetching apps after swap:', error)
      } finally {
        if (isMounted) {
          setAppsRefetching(false)
          setAppsWithReviewDataLoaded(true)
        }
      }
    }

    refetchAppsWithFullData()
    return () => {
      isMounted = false
    }
  }, [currentAppSlugs])

  // Reset AI states when compared apps change (prevents stale content across swaps).
  //
  // CRITICAL ORDERING AND GUARD (do not revert without understanding):
  //
  // 1. prevAppSlugsRef guard: Without this, the effect fires on INITIAL MOUNT
  //    (React runs all effects on mount). It would set storedComparisonCheckPending=true,
  //    and in dynamic mode nothing would clear it — permanently blocking the AI TLDR
  //    effect (infinite shimmer). The ref ensures the effect only fires on CHANGES.
  //
  // 2. Declaration order: This effect MUST be declared BEFORE the storedComparisonCheck
  //    effect below. React runs effects in declaration order within a single render.
  //    When the user swaps back to the original slug order (!appsHaveBeenSwapped),
  //    this effect sets storedComparisonCheckPending=true, then the stored check
  //    sets it to false. In React 18's batched updates, the LAST write wins.
  //    If this effect were declared AFTER the check, its true would win → stuck.
  useEffect(() => {
    if (prevAppSlugsRef.current === currentAppSlugs) return
    prevAppSlugsRef.current = currentAppSlugs
    setAiGeneratedVerdict(null)
    setAiVerdictLoading(false)
    setAiGeneratedSummary(null)
    setStoredComparisonCheckPending(true)
  }, [currentAppSlugs])

  // Check if there's a stored comparison for the current apps after swapping.
  // Gates AI effects via storedComparisonCheckPending — without this, the AI TLDR
  // and verdict effects would fire with a stale currentComparison value before we
  // know whether a stored comparison exists for the new app combination.
  //
  // queryComparisonByAppSlugs makes 3 sequential network calls (get IDs → query
  // junction table → fetch comparison), so this takes meaningful time. The pending
  // flag blocks AI generation during this window.
  //
  // Deps use currentAppSlugs (not apps) to avoid re-firing when setApps changes
  // the reference without changing the slugs (same reason as the refetch effect).
  // Visual order is preserved: apps state maintains the user's chosen order,
  // while stored content loads from the DB regardless of canonical order.
  useEffect(() => {
    if (!appsHaveBeenSwapped) {
      // Restore the original SSG comparison (may have been nulled by a prior swap)
      setCurrentComparison(isDynamicComparison ? null : ssgComparison ?? null)
      setStoredComparisonCheckPending(false)
      return
    }

    let isMounted = true

    async function checkForStoredComparison() {
      try {
        const slugs = currentAppSlugs.split('-vs-').filter(Boolean)
        const comparison = await queryComparisonByAppSlugs({ slugs })

        if (isMounted) {
          setCurrentComparison(comparison)
          if (comparison) {
            const comparisonApps = comparison.comparisonApps?.docs as ComparisonAppsApp[]
            if (comparisonApps) {
              const orderedApps = comparisonApps
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((ca) => ca.app as App)
              // Merge, don't replace: this races the full-app refetch, and the comparison's
              // app populate is narrower. Replacing could drop partnershipTier/deals/
              // affiliateLink and flip the winner styling and primary CTA.
              setAppsWithReviewData((prev) =>
                orderedApps.map((app) => {
                  const existing = prev.find((a) => a.id === app.id)
                  return existing ? { ...existing, ...app } : app
                }),
              )
            }
          }
        }
      } catch (error) {
        logger.error('Error checking for stored comparison:', error)
        if (isMounted) {
          setCurrentComparison(null)
        }
      } finally {
        if (isMounted) setStoredComparisonCheckPending(false)
      }
    }

    checkForStoredComparison()
    return () => {
      isMounted = false
    }
    // ssgComparison is in deps so that when the server re-renders with fresh
    // data (e.g. on live-preview router.refresh() after an admin autosave), the
    // local `currentComparison` state resyncs to the new prop instead of staying
    // pinned to the value snapshotted at mount.
  }, [currentAppSlugs, appsHaveBeenSwapped, ssgComparison, isDynamicComparison])

  // ==========================================================================
  // EFFECTS - Efficiency Points
  // Computed from appsWithReviewData (already fetched) instead of a separate
  // client-side query. The previous queryPayload call used nested where filters
  // at depth 0 which didn't resolve client-side, always returning 0 points.
  // ==========================================================================

  useEffect(() => {
    if (!primaryCategory) return
    if (appsWithReviewData.length === 0) return

    const pointsMap: Record<App['id'], number | undefined> = {}
    const criteriaMap: Record<App['id'], CriterionWithRating[]> = {}

    for (const app of apps) {
      const appData = appsWithReviewData.find((a) => a.id === app.id)
      // Skip apps not yet in appsWithReviewData (still loading after a swap).
      // Leaving them out of pointsMap lets the UI show a dash ("-") instead of 0.
      if (!appData) continue
      const review = getPrimaryReview(appData)
      const ratings = review?.reviewCategoryCriterionRating?.docs as
        | ReviewCategoryCriterionRating[]
        | undefined

      // Filter ratings to the primary category's criteria
      const categoryRatings =
        ratings?.filter((r) => {
          if (r.isApplicable === false) return false
          const cc = r.categoryCriterion as CategoryCriteriaCriterion | undefined
          const category = cc?.category
          const categoryId = typeof category === 'number' ? category : category?.id
          return categoryId === primaryCategory.id
        }) ?? []

      const points = categoryRatings.reduce((acc, r) => {
        if (r.rating && r.rating >= 5) return acc + 1
        return acc
      }, 0)

      const criteriaWithRatings = extractUniqueCriteriaFromApp(
        appData ?? app,
        ratings,
        primaryCategory.id,
      )

      // Unrated apps (no applicable ratings in category) → undefined → renders "-"
      // Rated apps with 0 points (all ratings < 5) → 0 → renders "0"
      pointsMap[app.id] = categoryRatings.length === 0 ? undefined : points
      criteriaMap[app.id] = criteriaWithRatings
    }

    setAppCategoryEfficiencyPoints(pointsMap)
    setAppCategoryCriteriaWithRatings(criteriaMap)
  }, [apps, primaryCategory, appsWithReviewData])

  // ==========================================================================
  // MEMOS - Criteria Extraction
  // Extract unique criteria from apps and build lookup maps for rendering.
  // ==========================================================================

  // Enrich apps with full review data for rating-dependent UI (winner/draw).
  // In dynamic mode, `apps` comes from fetchAppsBySlugsLight which lacks
  // reviewCategoryCriterionRating. appsWithReviewData has the full chain.
  const appsForScoring = useMemo(
    () => apps.map((app) => appsWithReviewData.find((a) => a.id === app.id) ?? app),
    [apps, appsWithReviewData],
  )

  const { orderedAppRecs, secondaryApps, criteria, appsByCriterionId, categoryIdByCriterionId } =
    useMemo(() => {
      const appCategoryRecommendations = appsForScoring
        .map((app) => convertAppToAppCategoryRecommendation(app, primaryCategory?.id))
        .filter((rec): rec is AppCategoryRecommendation => rec !== null)

      // Compare pages list every compared app, including ones /best moves to notable
      // mentions as discontinued
      const { orderedAppRecs, secondaryApps } = orderAppsByRecommendation(
        appCategoryRecommendations,
        true,
        { includeDiscontinued: true },
      )

      const uniqueCriteriaIds = new Set<Criterion['id']>()
      const uniqueCriteria: Criterion[] = []
      const criterionAppsMap = new Map<Criterion['id'], App[]>()
      const criterionAppIdsMap = new Map<Criterion['id'], Set<App['id']>>()
      const appCriteriaMap = new Map<App['id'], Set<Criterion['id']>>()
      const criterionCategoryMap = new Map<Criterion['id'], Category['id']>()

      const overlappingCategoryIds = new Set(categories.map((c) => c.id))

      appsForScoring.forEach((app) => {
        const acrs = app.categoryRecommendations?.docs as AppCategoryRecommendation[]
        if (!acrs?.length) return

        // Initialize app criteria set once
        if (!appCriteriaMap.has(app.id)) {
          appCriteriaMap.set(app.id, new Set())
        }

        acrs.forEach((acr) => {
          const category = acr.category as Category
          if (!overlappingCategoryIds.has(category.id)) return

          const categoryCriteria = category.categoryCriteria?.docs as CategoryCriteriaCriterion[]
          if (!categoryCriteria?.length) return

          categoryCriteria.forEach((cc) => {
            const criterion = cc.criterion as Criterion
            if (!criterion?.id) return

            const criterionId = criterion.id

            // Add to app criteria (Set prevents duplicates automatically)
            appCriteriaMap.get(app.id)!.add(criterionId)

            // Add to unique criteria if new
            if (!uniqueCriteriaIds.has(criterionId)) {
              uniqueCriteriaIds.add(criterionId)
              uniqueCriteria.push(criterion)
              criterionCategoryMap.set(criterionId, category.id)
            } else if (
              primaryCategory &&
              category.id === primaryCategory.id &&
              criterionCategoryMap.get(criterionId) !== primaryCategory.id
            ) {
              // A criterion like "Ease of Learning" can exist in multiple overlapping
              // categories (e.g., PM and Todo List). categoryIdByCriterionId is used by
              // Editor's Verdict to look up ratings via determineWinner. If the map
              // points to a secondary category where the apps have no ratings, the
              // lookup returns null for both apps → false draw. Upgrading to the primary
              // category ensures ratings are found where they actually exist.
              criterionCategoryMap.set(criterionId, primaryCategory.id)
            }

            // Add to criterion apps map
            if (!criterionAppsMap.has(criterionId)) {
              criterionAppsMap.set(criterionId, [])
              criterionAppIdsMap.set(criterionId, new Set())
            }
            if (!criterionAppIdsMap.get(criterionId)!.has(app.id)) {
              criterionAppsMap.get(criterionId)!.push(app)
              criterionAppIdsMap.get(criterionId)!.add(app.id)
            }
          })
        })
      })

      const finalAppCriteriaMap = new Map<App['id'], Criterion[]>()
      for (const app of appsForScoring) {
        const criterionIds = appCriteriaMap.get(app.id) ?? new Set<Criterion['id']>()
        finalAppCriteriaMap.set(
          app.id,
          uniqueCriteria.filter((c) => criterionIds.has(c.id)),
        )
      }

      return {
        orderedAppRecs,
        secondaryApps,
        criteria: uniqueCriteria,
        appsByCriterionId: Object.fromEntries(criterionAppsMap),
        categoryIdByCriterionId: Object.fromEntries(criterionCategoryMap),
      }
    }, [appsForScoring, primaryCategory, categories])

  // Winner first, matching the Summary ranking; apps without a primary-category
  // recommendation keep their URL order after the ranked ones.
  const sidebarApps = useMemo(() => {
    const rankedIds = orderedAppRecs
      .map((rec) => (isPopulatedObject<App>(rec.app) ? rec.app.id : null))
      .filter((id): id is App['id'] => id !== null)
    const position = (app: App): number => {
      const index = rankedIds.indexOf(app.id)
      return index === -1 ? rankedIds.length : index
    }
    return [...apps].sort((a, b) => position(a) - position(b))
  }, [apps, orderedAppRecs])

  // Compare-page gating rule:
  // Only check criteria from the primary category — secondary overlapping
  // categories may have criteria defined but no ratings, which would
  // incorrectly block the per-criterion comparison view.
  const hasCompleteCriteriaData = useMemo(() => {
    if (criteria.length === 0) return false
    if (appsForScoring.length < 2) return false

    const primaryCriteria = primaryCategory
      ? criteria.filter((c) => categoryIdByCriterionId[c.id] === primaryCategory.id)
      : criteria

    if (primaryCriteria.length === 0) return false

    const ratedCriterionIdsByApp = new Map<App['id'], Set<Criterion['id']>>()

    appsForScoring.forEach((app) => {
      const review = getPrimaryReview(app)
      const ratings = review?.reviewCategoryCriterionRating?.docs as
        | ReviewCategoryCriterionRating[]
        | undefined

      const criterionIds = new Set<Criterion['id']>()
      ratings?.forEach((rating) => {
        const cc = rating.categoryCriterion as CategoryCriteriaCriterion | undefined
        const crit = cc?.criterion as Criterion | undefined
        const children = rating?.summary?.root?.children as Block[] | undefined
        if (crit?.id && hasRichTextContent(children)) {
          // Only count this rating if it's from the category mapped to this criterion
          const expectedCatId = categoryIdByCriterionId[crit.id]
          if (expectedCatId !== undefined) {
            const catId =
              typeof cc?.category === 'number'
                ? cc.category
                : (cc?.category as Category | undefined)?.id
            if (catId !== undefined && catId !== expectedCatId) return
          }
          criterionIds.add(crit.id)
        }
      })

      ratedCriterionIdsByApp.set(app.id, criterionIds)
    })

    return primaryCriteria.every((criterion) => {
      const appsForCriterion = appsByCriterionId[criterion.id] ?? []
      if (appsForCriterion.length !== appsForScoring.length) return false

      return appsForScoring.every(
        (app) => ratedCriterionIdsByApp.get(app.id)?.has(criterion.id) === true,
      )
    })
  }, [criteria, appsForScoring, appsByCriterionId, primaryCategory, categoryIdByCriterionId])

  // ==========================================================================
  // EFFECTS - AI Summary Generation
  // Fetches AI-generated TLDR for dynamic comparisons (no stored record).
  // Per-criterion summaries are fetched individually by CriterionAiSummary.
  // ==========================================================================

  useEffect(() => {
    if (apps.length < 2) return
    // Wait for stored comparison check to resolve before deciding to generate AI content
    if (storedComparisonCheckPending) return

    // Only fetch TLDR for dynamic comparisons (no stored comparison)
    const hasStoredComparison = currentComparison !== null
    if (hasStoredComparison) {
      setAiSummaryLoading(false)
      return
    }

    // Skip if we already have an AI summary
    if (aiGeneratedSummary) {
      setAiSummaryLoading(false)
      return
    }

    let isMounted = true

    // Uses server action to bypass Vercel password protection on preview deployments
    async function fetchDynamicTldr() {
      setAiSummaryLoading(true)
      try {
        const appSlugs = apps.map((app) => app.slug).filter((slug): slug is string => !!slug)
        const data = await generateDynamicComparison({ appSlugs })

        if (isMounted && data.success && data.summary) {
          setAiGeneratedSummary(data.summary)
        }
      } catch (error) {
        logger.error('Error generating AI TLDR summary:', error)
      } finally {
        if (isMounted) setAiSummaryLoading(false)
      }
    }

    fetchDynamicTldr()
    return () => {
      isMounted = false
    }
  }, [apps, currentComparison, aiGeneratedSummary, storedComparisonCheckPending])

  // Verdict fallback triggers when criterion coverage is incomplete.
  // This includes cases where apps share categories but one or more apps
  // are missing ratings on one or more overlapping criteria.
  useEffect(() => {
    if (apps.length < 2) return
    // Wait for stored comparison check before deciding to generate AI content
    if (storedComparisonCheckPending) return
    // Wait for review data before evaluating criterion coverage — in dynamic mode
    // it starts false (empty array) and would trigger a premature OpenAI call
    if (!appsWithReviewDataLoaded) return
    // If stored verdict content exists, do not generate AI fallback.
    if (currentComparison && hasRichTextContent(verdictSummary)) return
    if (hasCompleteCriteriaData) return
    if (appFocus) return
    if (aiGeneratedVerdict) return

    let isMounted = true

    async function fetchVerdictFallback() {
      setAiVerdictLoading(true)
      try {
        const appSlugs = apps.map((app) => app.slug).filter((slug): slug is string => !!slug)
        const categoryIds = categories.map((c) => c.id)
        const data = await generateVerdictFallback({ appSlugs, categoryIds })

        if (isMounted && data.success && data.verdict) {
          setAiGeneratedVerdict(data.verdict)
        }
      } catch (error) {
        logger.error('Error generating AI verdict fallback:', error)
      } finally {
        if (isMounted) setAiVerdictLoading(false)
      }
    }

    fetchVerdictFallback()
    return () => {
      isMounted = false
    }
  }, [
    apps,
    categories,
    storedComparisonCheckPending,
    appsWithReviewDataLoaded,
    currentComparison,
    verdictSummary,
    hasCompleteCriteriaData,
    appFocus,
    aiGeneratedVerdict,
  ])

  // Recalculate alternatives/trending when comparison apps change via swapping.
  // Can't refetch server-side data client-side, so we filter the SSG values against
  // current apps and hide everything if any current app is a partner tier.
  useEffect(() => {
    const partnerTiers = new Set(['gold', 'silver', 'platinum'])
    const anyPartner = apps.some(
      (app) => app.partnershipTier && partnerTiers.has(app.partnershipTier),
    )

    if (anyPartner) {
      setAlternativeApps([])
      setTrendingApp(undefined)
      return
    }

    const currentSlugs = new Set(apps.map((a) => a.slug))
    const filteredAlts = (ssgAlternativeApps ?? []).filter((alt) => {
      const altApp = isPopulatedObject<App>(alt.app) ? alt.app : null
      return altApp !== null && !currentSlugs.has(altApp.slug)
    })
    setAlternativeApps(filteredAlts)

    // With in-category alternatives, no trending fallback. Otherwise client-fetch a global
    // platinum/gold partner (weighted 60/40) — done here, not server-side, so it works on
    // dynamic compare pages too and re-rolls on refresh. Excludes the compared apps.
    if (filteredAlts.length > 0) {
      setTrendingApp(undefined)
      return
    }

    let cancelled = false
    const currentAppIds = new Set(apps.map((a) => a.id))
    async function loadTrending() {
      const candidates = await fetchTrendingPartnerCandidates()
      const eligible = candidates.filter((candidate) => !currentAppIds.has(candidate.id))
      const picked = pickWeightedPartner(eligible)
      if (!cancelled) {
        setTrendingApp(picked ? ({ app: picked } as AppCategoryRecommendation) : undefined)
        setTrendingLoaded(true)
      }
    }
    setTrendingLoaded(false)
    loadTrending()
    return () => {
      cancelled = true
    }
  }, [currentAppSlugs])

  const filteredApps = useMemo(() => {
    return appFocus ? apps.filter((app) => app.name === appFocus) : apps
  }, [appFocus, apps])

  // ==========================================================================
  // SECTION BUILDERS - Content Sections
  // Build the content sections that make up the page layout.
  // Each section is an object with title, content, and optional children.
  // ==========================================================================

  const hasMultipleCategories = categories.length > 1
  const hasStoredSummary = currentComparison !== null && summary && summary.length > 0
  // Only fall back to AI summary in dynamic mode (no comparison record).
  // When a stored comparison exists but its TLDR is empty, hide the summary
  // rather than generating one — avoids showing AI content for curated pages.
  const shouldShowAiSummary = !hasStoredSummary && currentComparison === null
  const shouldShowFocusedAppSummary =
    !!appFocus && filteredApps.some((app) => Boolean(tldr[app.id]))

  // Helper: Fetch TLDR for focused app (single app view)
  useEffect(() => {
    let isMounted = true
    setTldr({})

    const fetchAndSetTldr = async (appId: App['id']) => {
      try {
        const { docs: reviews } = await queryPayload({
          collection: 'review',
          select: {
            tldr: true,
          },
          where: {
            app: { equals: appId },
          },
          depth: 0,
          pagination: false,
          cacheKey: `query:review:tldr:${appId}`,
        })

        if (!isMounted) return
        const [review] = reviews
        if (!review) return
        setTldr((prev) => ({
          ...prev,
          [appId]: Array.isArray(review.tldr?.root?.children)
            ? (review.tldr.root.children as Block[])
            : null,
        }))
      } catch (error) {
        logger.error('Error fetching TLDR for app:', appId, error)
        if (isMounted) setTldr((prev) => ({ ...prev, [appId]: null }))
      }
    }

    apps.forEach((app) => fetchAndSetTldr(app.id))

    return () => {
      isMounted = false
    }
  }, [appIds.join(',')])

  // Comparison Summary Section
  const comparisonSection =
    hasStoredSummary ||
    shouldShowAiSummary ||
    (!hasMultipleCategories && !!primaryCategory) ||
    shouldShowFocusedAppSummary
      ? [
          {
            // Per-app tabs: when review.tldr is null, show just the app name
            // (not "{Name} Summary") and skip the gap so AppRanking sits flush

            title: (() => {
              if (filteredApps.length !== 1) return `Comparison Summary`
              const focusedApp = filteredApps[0]
              const appTldr = tldr[focusedApp.id]
              return appTldr ? `${focusedApp.name} Summary` : focusedApp.name
            })(),
            slug: appFocus ? `${toSlug(appFocus)}-summary` : 'summary',
            content: (
              <div className="flex flex-col gap-5" data-testid="comparison-summary">
                {hasStoredSummary && !appFocus ? (
                  <RichText content={summary as Block[]} />
                ) : shouldShowAiSummary && !appFocus ? (
                  aiSummaryLoading ? (
                    <TextPlaceholder rows={1} />
                  ) : aiGeneratedSummary ? (
                    <p>{aiGeneratedSummary}</p>
                  ) : null
                ) : null}
                {apps.map((app) => (
                  <div key={app.id} className={cn(appFocus === app.name ? 'block' : 'hidden')}>
                    {tldr[app.id] ? <RichText content={tldr[app.id]!} /> : null}
                  </div>
                ))}
                {/* Intentional: single-category AppRanking lives in summary for aesthetics */}
                {!hasMultipleCategories && primaryCategory ? (
                  <AppRanking
                    apps={orderedAppRecs}
                    otherApps={secondaryApps}
                    focus={appFocus}
                    category={primaryCategory}
                    rankByAppId={rankByAppId}
                  />
                ) : null}
                {!hasMultipleCategories && currentBestCta ? <BestCta {...currentBestCta} /> : null}
              </div>
            ),
          },
        ]
      : []

  // At a Glance Section
  // The reducer always runs — per-category `hasEnoughRatings` gates CompareCriterias inside.
  // This ensures AppRanking still renders even when no criteria ratings exist (e.g. Comet vs Dia).
  const glanceSections = useMemo(
    () =>
      categories.reduce(
        (acc, category) => {
          // Compute section-specific ACRs so recommendation, bestFor, and ranking
          // reflect this category — not the primary category
          const sectionAppRecs = hasMultipleCategories
            ? appsForScoring
                .map((app) => convertAppToAppCategoryRecommendation(app, category.id))
                .filter((rec): rec is AppCategoryRecommendation => rec !== null)
            : []

          // Use all overlapping ACRs for section presence checks so notable mentions
          // don't prevent an At-a-Glance section from rendering.
          const sectionOverlappingRecs = sectionAppRecs.filter((rec, index, recs) => {
            if (typeof rec.app === 'number' || !rec.app?.id) return false
            const appId = rec.app.id
            return (
              recs.findIndex((candidate) => {
                if (typeof candidate.app === 'number' || !candidate.app?.id) return false
                return candidate.app.id === appId
              }) === index
            )
          })
          const { orderedAppRecs: sectionOrderedRecs, secondaryApps: sectionSecondaryApps } =
            hasMultipleCategories
              ? orderAppsByRecommendation(sectionAppRecs, true, { includeDiscontinued: true })
              : { orderedAppRecs: [], secondaryApps: [] }

          const hasAtLeastTwoApps = hasMultipleCategories
            ? sectionOverlappingRecs.length > 1
            : orderedAppRecs.length > 1

          if (!hasAtLeastTwoApps) return acc

          // Check if 2+ apps have rating data for this category's criteria
          // Use CategoryCriteriaCriterion IDs (not criterion IDs) to avoid
          // false positives when the same criterion appears in multiple categories
          const categoryCriteria = category.categoryCriteria?.docs as
            | CategoryCriteriaCriterion[]
            | undefined
          let hasEnoughRatings = true
          if (categoryCriteria) {
            const categoryCriterionIds = new Set(
              categoryCriteria
                .map((cc) => cc.id)
                .filter((id): id is CategoryCriteriaCriterion['id'] => id !== undefined),
            )
            const appsWithRatingsInCategory = appsForScoring.filter((app) => {
              const review = getPrimaryReview(app)
              const ratings = review?.reviewCategoryCriterionRating?.docs as
                | ReviewCategoryCriterionRating[]
                | undefined
              return ratings?.some((r) => {
                const cc = r.categoryCriterion as CategoryCriteriaCriterion | undefined
                const ccId = typeof cc === 'number' ? cc : cc?.id
                return ccId !== undefined && categoryCriterionIds.has(ccId)
              })
            })
            hasEnoughRatings = appsWithRatingsInCategory.length >= 2
          }

          // Single-category: skip section entirely when no criteria ratings exist
          if (!hasMultipleCategories && !hasEnoughRatings) return acc

          const isPrimary = category.id === primaryCategory?.id
          const sectionBestCta = getBestCta(category.id)

          acc.push({
            title: appFocus
              ? hasMultipleCategories
                ? `${appFocus}: ${category.name}`
                : `${appFocus} At a Glance`
              : hasMultipleCategories
                ? category.name
                : `At a Glance`,
            collapsed: hasMultipleCategories ? !isPrimary : undefined,
            titleBadge:
              hasMultipleCategories && isPrimary ? (
                <Badge style="subtle" className="hidden gap-[3px] xs:flex" nested>
                  Main <span className="hidden lg:inline-flex">Category</span>
                </Badge>
              ) : undefined,
            content: (
              <div className="flex flex-col gap-4" data-testid="at-a-glance">
                {generateIntro(filteredApps, category)}
                {hasMultipleCategories && category ? (
                  <AppRanking
                    apps={sectionOrderedRecs}
                    otherApps={sectionSecondaryApps}
                    focus={appFocus}
                    category={category}
                  />
                ) : null}
                {hasMultipleCategories && sectionBestCta ? <BestCta {...sectionBestCta} /> : null}
                {hasEnoughRatings && (
                  <CompareCriterias
                    apps={appsForScoring}
                    focus={appFocus}
                    categoryId={category.id}
                    appCategoryEfficiencyPoints={appCategoryEfficiencyPoints}
                    isPrimary={isPrimary && hasCompleteCriteriaData}
                  />
                )}
              </div>
            ),
          })

          return acc
        },
        [] as Array<{
          title: string
          icon?: ReactNode
          content: ReactNode
          collapsed?: boolean
          titleBadge?: ReactNode
        }>,
      ),
    [
      filteredApps,
      appFocus,
      categories,
      appsForScoring,
      hasMultipleCategories,
      orderedAppRecs,
      secondaryApps,
      appCategoryEfficiencyPoints,
      primaryCategory,
      getBestCta,
    ],
  )

  // --- Video Section ---
  const hasVideoData = videoData !== undefined
  const videos = hasVideoData ? videoData.map((e) => e.video) : []
  const videoAppsMap = Object.fromEntries(
    (hasVideoData ? videoData : []).map((e) => [e.video.id, e.videoApps]),
  )
  const videoSection = hasVideoData
    ? [
        {
          title: `Comparison Video${videos.length > 1 ? 's' : ''}`,
          content: <Videos videos={videos} videoAppsMap={videoAppsMap} />,
        },
      ]
    : []

  // --- Summary Section ---
  const summarySection =
    !appFocus && primaryCategory
      ? [
          {
            title: `Summaries`,
            content: <AppList apps={apps} label="Summary" categoryId={primaryCategory.id} />,
          },
        ]
      : []

  // --- Editor's Verdict Section ---
  // CriterionChild renders individual criterion content within the verdict section.
  // Shows single-app review content OR multi-app comparison (stored/AI-generated).
  const CriterionChild = useCallback(
    (criterion: Criterion, parentId: string, childId: string) => {
      const appResults = appsByCriterionId[criterion.id]
      if (appFocus && !appResults.some((app) => app.name === appFocus)) return null

      // Filter to focused app if one is selected
      const displayApps = appFocus ? appResults.filter((app) => app.name === appFocus) : appResults

      const sectionIdString = appFocus
        ? `${toSlug(appFocus)}-${parentId}-${childId}`
        : `${parentId}-${childId}`

      return (
        <Section
          key={sectionIdString}
          sectionId={sectionIdString}
          title={criterion.name ?? 'Untitled'}
          header={
            <CriterionOutcome
              apps={appResults}
              criterionId={criterion.id}
              categoryId={categoryIdByCriterionId[criterion.id]}
              appCategoryEfficiencyPoints={appCategoryEfficiencyPoints}
              focus={appFocus}
            />
          }
          isChild
        >
          <div className="flex flex-col gap-4">
            {/* this is the category criteria summary for the focused app */}
            {displayApps.length === 1
              ? displayApps?.map((app) => {
                  // Use appsWithReviewData to find the review summary
                  // In dynamic mode, this data is fetched client-side
                  const appWithReviewData = appsWithReviewData.find((a) => a.id === app.id)
                  const review = getPrimaryReview(appWithReviewData)
                  const ratings = review?.reviewCategoryCriterionRating?.docs as
                    | ReviewCategoryCriterionRating[]
                    | undefined
                  const expectedCatId = categoryIdByCriterionId[criterion.id]
                  const matchingRating = ratings?.find((r) => {
                    const cc = r.categoryCriterion as CategoryCriteriaCriterion | undefined
                    const crit = cc?.criterion as Criterion | undefined
                    if (crit?.id !== criterion.id) return false
                    if (expectedCatId !== undefined) {
                      const catId =
                        typeof cc?.category === 'number'
                          ? cc.category
                          : (cc?.category as Category | undefined)?.id
                      if (catId !== undefined && catId !== expectedCatId) return false
                    }
                    return true
                  })
                  const ratingSummary = matchingRating?.summary

                  if (!ratingSummary?.root?.children) return null

                  return (
                    <div key={`app-${app.id}`} className="flex flex-col gap-1">
                      <RichText content={ratingSummary.root.children as Block[]} />
                    </div>
                  )
                })
              : // Show comparison summary for this criterion when multiple apps selected
                // Priority: 1) Stored published summary from DB, 2) Batched AI summary, 3) Nothing if no rating data
                (() => {
                  // Check for stored published summary first
                  const expectedCatId = categoryIdByCriterionId[criterion.id]
                  const storedSummary = storedAiSummaries.find((doc) => {
                    const cc = doc.categoryCriterion as CategoryCriteriaCriterion | undefined
                    const crit = cc?.criterion as Criterion | undefined
                    if (crit?.id !== criterion.id) return false
                    if (expectedCatId !== undefined) {
                      const catId =
                        typeof cc?.category === 'number'
                          ? cc.category
                          : (cc?.category as Category | undefined)?.id
                      if (catId !== undefined && catId !== expectedCatId) return false
                    }
                    return true
                  })

                  if (storedSummary?.summary) {
                    const paragraphs = storedSummary.summary
                      .replace(/\r\n/g, '\n')
                      .split(/\n{2,}/)
                      .map((p) => p.trim())
                      .filter(Boolean)
                    return (
                      <div key={`stored-summary-${criterion.id}`} className="flex flex-col gap-4">
                        {paragraphs.map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }

                  // Pre-check: Count how many apps have rating data for this criterion
                  // Only attempt AI generation if at least 2 apps have ratings
                  const appsWithCriterionRatings = displayApps.filter((app) => {
                    const matchingAppData = appsWithReviewData.find((a) => a.id === app.id)
                    const review = getPrimaryReview(matchingAppData)
                    const ratings = review?.reviewCategoryCriterionRating?.docs as
                      | ReviewCategoryCriterionRating[]
                      | undefined
                    return ratings?.some((r) => {
                      const cc = r.categoryCriterion as CategoryCriteriaCriterion | undefined
                      const crit = cc?.criterion as Criterion | undefined
                      return crit?.id === criterion.id
                    })
                  })

                  // Skip AI generation if fewer than 2 apps have rating data
                  if (appsWithCriterionRatings.length < 2) {
                    return null
                  }

                  // Fall back to AI-generated summary
                  return (
                    <CriterionAiSummary
                      key={`ai-summary-${criterion.id}`}
                      criterionId={criterion.id}
                      appSlugs={apps.map((a) => a.slug).filter((slug): slug is string => !!slug)}
                      cacheKeyPrefix={criterionSummaryCacheKey}
                    />
                  )
                })()}
          </div>
        </Section>
      )
    },

    [
      appFocus,
      appsByCriterionId,
      categoryIdByCriterionId,
      criteria,
      appsWithReviewData,
      apps,
      criterionSummaryCacheKey,
      storedAiSummaries,
      appCategoryEfficiencyPoints,
      primaryCategory,
    ],
  )

  const verdictSections = useMemo(() => {
    // Full verdict with per-criterion children (works for both comparison and app tabs)
    if (criteria.length > 0) {
      const verdictContent =
        verdictSummary.length > 0 && !appFocus ? (
          <RichText content={verdictSummary.filter((block) => block.type === 'paragraph')} />
        ) : null

      const allowCriteriaChildren = appFocus || hasCompleteCriteriaData

      const filteredChildren = allowCriteriaChildren
        ? criteria
            .filter((criterion) => {
              // Skip criteria that would render blank sections
              if (appFocus) {
                // Only show criteria from the primary category (matches comparison tab behavior)
                if (categoryIdByCriterionId[criterion.id] !== primaryCategory?.id) return false

                // App tab: check if focused app has rating data for this criterion
                const appResults = appsByCriterionId[criterion.id]
                const focusedInResults = appResults?.some((app) => app.name === appFocus)
                if (!focusedInResults) return false
                const focusedApp = appResults.find((app) => app.name === appFocus)
                if (!focusedApp) return false
                const appWithReviewData = appsWithReviewData.find((a) => a.id === focusedApp.id)
                const review = getPrimaryReview(appWithReviewData)
                const ratings = review?.reviewCategoryCriterionRating?.docs as
                  | ReviewCategoryCriterionRating[]
                  | undefined
                return ratings?.some((r) => {
                  const cc = r.categoryCriterion as CategoryCriteriaCriterion | undefined
                  const crit = cc?.criterion as Criterion | undefined
                  if (crit?.id !== criterion.id) return false
                  // Verify rating is from the primary category, not a same-named criterion in another category
                  const catId =
                    typeof cc?.category === 'number'
                      ? cc.category
                      : (cc?.category as Category | undefined)?.id
                  if (catId !== undefined && primaryCategory && catId !== primaryCategory.id)
                    return false
                  return hasRichTextContent(r?.summary?.root?.children as Block[] | undefined)
                })
              }
              // Comparison view: show if stored summary exists or 2+ apps have ratings
              const hasStoredSummary = storedAiSummaries.some((doc) => {
                const cc = doc.categoryCriterion as CategoryCriteriaCriterion | undefined
                const crit = cc?.criterion as Criterion | undefined
                return crit?.id === criterion.id && doc.summary && doc.summary.trim().length > 0
              })
              if (hasStoredSummary) return true
              const expectedCatId = categoryIdByCriterionId[criterion.id]
              const appsWithRatings = (appsByCriterionId[criterion.id] ?? []).filter((app) => {
                const review = getPrimaryReview(app)
                const ratings = review?.reviewCategoryCriterionRating?.docs as
                  | ReviewCategoryCriterionRating[]
                  | undefined
                return ratings?.some((r) => {
                  const cc = r.categoryCriterion as CategoryCriteriaCriterion | undefined
                  const crit = cc?.criterion as Criterion | undefined
                  if (crit?.id !== criterion.id) return false
                  // Verify rating is from the mapped category, not a same-named criterion in another category
                  if (expectedCatId !== undefined) {
                    const catId =
                      typeof cc?.category === 'number'
                        ? cc.category
                        : (cc?.category as Category | undefined)?.id
                    if (catId !== undefined && catId !== expectedCatId) return false
                  }
                  return hasRichTextContent(r?.summary?.root?.children as Block[] | undefined)
                })
              })
              return appsWithRatings.length >= 2
            })
            .map((criterion) => {
              const criteriaTitle = criterion.name ?? 'Untitled'
              const parentId = `editors-verdict`
              const childId = toSlug(criteriaTitle)
              const sectionSlug = appFocus
                ? `${toSlug(appFocus)}-${parentId}-${childId}`
                : `${parentId}-${childId}`
              return {
                criterionId: criterion.id,
                title: criterion.name ?? 'Untitled',
                slug: sectionSlug,
                content: CriterionChild(criterion, parentId, childId),
              }
            })
            .sort((a, b) => {
              // Sort by the order in primaryCategory.categoryCriteria.docs
              if (!primaryCategory) return 0
              const categoryCriteria = primaryCategory.categoryCriteria?.docs as
                | CategoryCriteriaCriterion[]
                | undefined
              if (!categoryCriteria) return 0

              const indexA = categoryCriteria.findIndex((cc) => {
                const crit = cc.criterion as Criterion | number | undefined
                const critId = typeof crit === 'number' ? crit : crit?.id
                return critId === a.criterionId
              })
              const indexB = categoryCriteria.findIndex((cc) => {
                const crit = cc.criterion as Criterion | number | undefined
                const critId = typeof crit === 'number' ? crit : crit?.id
                return critId === b.criterionId
              })

              // If not found, keep original order
              if (indexA === -1 && indexB === -1) return 0
              if (indexA === -1) return 1
              if (indexB === -1) return -1

              return indexA - indexB
            })
            .map(({ criterionId, ...rest }) => rest)
        : []

      // Only use criteria-based verdict if we have children to show, or verdict content on app tabs.
      // On comparison view with no children, fall through to AI verdict fallback.
      if (filteredChildren.length > 0) {
        return [
          {
            title: appFocus ? `${appFocus} Editor's Verdict` : `Editor's Verdict`,
            titleBadge: hasMultipleCategories ? (
              <Badge style="subtle" className="hidden gap-[3px] xs:flex" nested>
                Main <span className="hidden lg:inline-flex">Category</span>
              </Badge>
            ) : undefined,
            content: verdictContent ? (
              <div data-testid="editors-verdict">{verdictContent}</div>
            ) : (
              <div data-testid="editors-verdict" />
            ),
            children: filteredChildren,
          },
        ]
      }
    }

    // Stored verdict summary without per-criterion children
    // (covers cases where criteria don't exist or none passed filters)
    if (hasRichTextContent(verdictSummary) && !appFocus) {
      return [
        {
          title: `Editor's Verdict`,
          titleBadge: hasMultipleCategories ? (
            <Badge className="hidden sm:flex" nested>
              Main Category
            </Badge>
          ) : undefined,
          content: (
            <div data-testid="editors-verdict" className={DEMOTED_HEADING_STYLES}>
              <RichText content={demoteHeadings(verdictSummary)} />
            </div>
          ),
        },
      ]
    }

    // App tab fallback (no criteria or no ratings for focused app): show ACR summary -> review.summary
    if (appFocus) {
      const focusedApp = apps.find((app) => app.name === appFocus)
      if (!focusedApp) return []

      const appWithReviewData = appsWithReviewData.find((a) => a.id === focusedApp.id)
      const review = getPrimaryReview(appWithReviewData)
      const acrs = appWithReviewData?.categoryRecommendations?.docs as AppCategoryRecommendation[]
      const primaryAcr = acrs?.find((acr) => {
        const cat = acr.category as Category
        return cat.id === primaryCategory?.id
      })

      const acrSummaryContent = (primaryAcr?.summary as Record<string, unknown>)?.root as
        | { children?: Block[] }
        | undefined
      const reviewSummaryContent = (review?.summary as Record<string, unknown>)?.root as
        | { children?: Block[] }
        | undefined
      const summaryContent = acrSummaryContent?.children ?? reviewSummaryContent?.children

      if (!summaryContent?.length) return []

      return [
        {
          title: appFocus ? `${appFocus} Editor's Verdict` : `Editor's Verdict`,
          content: (
            <div data-testid="editors-verdict" className={DEMOTED_HEADING_STYLES}>
              <RichText content={demoteHeadings(summaryContent)} />
            </div>
          ),
        },
      ]
    }

    // Comparison view fallback (no criteria or no usable per-criterion data): AI-generated verdict
    return [
      {
        title: `Verdict`,
        content: (
          <div data-testid="ai-verdict">
            {aiVerdictLoading ? (
              <TextPlaceholder rows={4} />
            ) : aiGeneratedVerdict ? (
              <div className="flex flex-col gap-4">
                {aiGeneratedVerdict
                  .replace(/\r\n/g, '\n')
                  .split(/\n{2,}/)
                  .map((paragraph) => paragraph.trim())
                  .filter(Boolean)
                  .map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
              </div>
            ) : null}
          </div>
        ),
      },
    ]
  }, [
    appFocus,
    apps,
    appsWithReviewData,
    appsByCriterionId,
    storedAiSummaries,
    primaryCategory,
    verdictSummary,
    criteria,
    hasCompleteCriteriaData,
    hasMultipleCategories,
    CriterionChild,
    aiVerdictLoading,
    aiGeneratedVerdict,
  ])

  // Screenshots Section
  // EA-413: Old stacked layout — kept for rollback
  // const screenshots = apps.flatMap((obj) => obj.screenshots?.docs) as Media[]
  // const screenshotSection = screenshots.length > 0 ? [{ title: `Screenshots`, content: <Images images={apps.flatMap((obj) => obj.screenshots?.docs) as Media[]} /> }] : []

  const screenshotEntries = buildScreenshotEntries(appFocus ? filteredApps : apps)
  const screenshotTitle = appFocus ? `${appFocus} Screenshots` : 'Screenshots'
  const screenshotSection =
    screenshotEntries.length > 0
      ? [
          {
            title: screenshotTitle,
            content: <ScreenshotCarousel entries={screenshotEntries} alwaysShowThumbnails />,
          },
        ]
      : []

  // Alternatives Section
  const alternativesTitle = hasMultipleCategories
    ? `Alternatives`
    : `${primaryCategory?.name || ''} Alternatives`.trim()

  const alternativeMenu = (app: App) => {
    return generateMenu('compare', {
      apps: [app],
      relatedApps: relatedApps,
      setApps: setApps,
      options: {
        isComparable: true,
        ...(apps.length < 4 && { isAppendable: true }),
      },
    })
  }

  const filteredRelatedApps = relatedApps.filter((app) => !appIds.includes(app.id))

  const alternativesSection =
    filteredRelatedApps.length > 0 && primaryCategory
      ? [
          {
            title: alternativesTitle,
            content: (
              <AppList
                apps={filteredRelatedApps}
                menu={alternativeMenu}
                categoryId={primaryCategory.id}
              />
            ),
          },
        ]
      : []

  // ACR Alternatives Section — partner-filtered recommendations for non-partner comparisons
  const recommendedAlternativesSection =
    alternativeApps.length > 0 && primaryCategory && !appFocus
      ? [
          {
            title: 'Recommended Alternatives',
            content: (
              <AppRanking
                apps={alternativeApps}
                category={primaryCategory}
                featuredPlacement={FeaturedPlacement.ALTERNATIVES}
              />
            ),
          },
        ]
      : []

  // Trending partner — the client-picked fallback shown when there are no ACR alternatives.
  // Rendered as a content section, mutually exclusive with Recommended Alternatives above.
  // Skeleton reserves height until the client pick resolves (matches ReviewContainer -> no CLS).
  const anyComparePartner = apps.some(
    (a) => a.partnershipTier && ['gold', 'silver', 'platinum'].includes(a.partnershipTier),
  )
  const showTrendingSlot =
    !anyComparePartner && alternativeApps.length === 0 && !!primaryCategory && !appFocus
  const trendingAppSection =
    showTrendingSlot && (!trendingLoaded || (trendingApp && trendingApp.app))
      ? [
          {
            title: 'Trending Right Now',
            content:
              trendingApp && trendingApp.app ? (
                <AppRanking
                  apps={[trendingApp]}
                  category={primaryCategory as Category}
                  featuredPlacement={FeaturedPlacement.TRENDING}
                />
              ) : (
                <div
                  aria-hidden="true"
                  className="min-h-[72px] w-full animate-pulse rounded-xl border border-white/15 bg-white/5"
                />
              ),
          },
        ]
      : []

  // Combine All Sections
  const content = [
    ...comparisonSection,
    ...glanceSections,
    ...videoSection,
    ...recommendedAlternativesSection,
    ...trendingAppSection,
    ...verdictSections,
    ...screenshotSection,
    ...summarySection,
    ...alternativesSection,
  ]

  return (
    <>
      <Meta
        modifiedTimestamp={modifiedTimestamp}
        publishedTimestamp={publishedTimestamp}
        parent={
          primaryCategory?.slug
            ? { label: primaryCategory.name, url: `/best/${primaryCategory.slug}` }
            : undefined
        }
        apps={apps.map((app) => ({ name: app.name, slug: app.slug ?? toSlug(app.name) }))}
        ftc={true}
      />
      <CompareHero
        apps={apps}
        appCategoryEfficiencyPoints={appCategoryEfficiencyPoints}
        appCategoryCriteriaWithRatings={appCategoryCriteriaWithRatings}
        efficiencyPointsLoading={
          appsRefetching || (isDynamicComparison && !appsWithReviewDataLoaded)
        }
        relatedApps={relatedApps}
        setAppsAction={setApps}
        menu={compareMenu}
        categories={categories}
        timestamps={{ modified: modifiedTimestamp, published: publishedTimestamp }}
        isSingleAppSelection={isSingleAppSelection}
      />

      {/* main.overlay supplies the margin-top that reveals the fixed hero, so it
          must render even in single-app mode. Instead of comparison content we
          render a light-mode placeholder body that lines up with the real page. */}
      <article ref={comparisonContentRef} id="content" className="overlay condensed">
        {isSingleAppSelection ? (
          <Container className="flex min-h-[55vh] flex-col items-center justify-center gap-3 py-24 text-center">
            <p className="font-heading text-24 font-semibold text-gray-950 dark:text-white">
              Select a second app to compare
            </p>
            <p className="max-w-md text-16 text-gray-500 dark:text-zinc-400">
              Pick an app in the second box above to see a full side-by-side comparison
              {apps[0]?.name ? ` with ${apps[0].name}` : ''}.
            </p>
          </Container>
        ) : (
          <>
            <Container
              sidebar={
                <>
                  <Tree
                    roots={content}
                    header={
                      <CompareSidebar
                        apps={sidebarApps}
                        menu={compareSidebarMenu}
                        menuByAppId={compareSidebarMenuByAppId}
                        category={primaryCategory}
                        bestCta={currentBestCta}
                      />
                    }
                  />
                  <PreferredSource invert={true} />
                  <div className="hidden flex-wrap items-center gap-y-2.5 md:flex">
                    {(modifiedTimestamp || publishedTimestamp) && (
                      <Timestamp
                        modified={modifiedTimestamp}
                        published={publishedTimestamp}
                        lead="Last updated on"
                        trail="by"
                        className="pr-1"
                      />
                    )}
                    <Authors authors={authors} condensed={true} eagerImage={true} />
                  </div>
                  <DealsTrending />
                </>
              }
              mobilePadding={false}
            >
              {apps.length > 1 && (
                <TabToggle
                  tabs={[
                    {
                      icon: <Icon name="clone-dashed" />,
                      text: 'Comparison',
                      action: () => {
                        const isComparison = appFocus === null
                        let sectionId = activeSection ?? 'content'

                        if (!isComparison) {
                          setAppFocus(null)
                          const strippedSection =
                            activeSection && activeSection !== 'content'
                              ? apps.reduce(
                                  (acc, a) => acc.replace(new RegExp(`^${toSlug(a.name)}-`), ''),
                                  activeSection,
                                )
                              : null

                          if (strippedSection) {
                            sectionId = strippedSection
                          }
                          lastTabScrollRef.current = sectionId
                        } else {
                          if (lastTabScrollRef.current === sectionId) {
                            sectionId = 'content'
                            lastTabScrollRef.current = null
                          } else {
                            lastTabScrollRef.current = sectionId
                          }
                        }

                        setTimeout(
                          () => {
                            scrollToSection({
                              id: sectionId ?? 'content',
                              offset: { mobile: 209, desktop: 154 },
                              animate: true,
                            })
                          },
                          isComparison ? 0 : 100,
                        )
                      },
                      disableScroll: true,
                    },
                    ...apps.map((app) => ({
                      icon: (
                        <AppIcon
                          icon={app.icon}
                          glyph={app.glyph}
                          name={app.name}
                          background={getBrandColors(app).backgroundColor}
                          size={24}
                        />
                      ),
                      text: app.name!,
                      action: () => {
                        const currentApp = appFocus === app.name
                        const excludedSlugs = [
                          'summaries',
                          'comparison-video',
                          'comparison-videos',
                          ...(primaryCategory?.name
                            ? [`${toSlug(primaryCategory.name)}-alternatives`]
                            : []),
                        ]

                        let sectionId = activeSection ?? 'content'

                        if (!currentApp) {
                          setAppFocus(app.name)
                          const appSlug = toSlug(app.name)
                          const strippedSection =
                            activeSection &&
                            activeSection !== 'content' &&
                            !excludedSlugs.includes(activeSection)
                              ? apps.reduce(
                                  (acc, a) => acc.replace(new RegExp(`^${toSlug(a.name)}-`), ''),
                                  activeSection,
                                )
                              : null

                          if (strippedSection) {
                            sectionId = `${appSlug}-${strippedSection}`
                          } else if (activeSection === 'content') {
                            sectionId = 'content'
                          }

                          lastTabScrollRef.current = sectionId
                        } else {
                          if (lastTabScrollRef.current === sectionId) {
                            sectionId = 'content'
                            lastTabScrollRef.current = null
                          } else {
                            lastTabScrollRef.current = sectionId
                          }
                        }

                        setTimeout(
                          () => {
                            scrollToSection({
                              id: sectionId ?? 'content',
                              offset: { mobile: 209, desktop: 154 },
                              animate: true,
                            })
                          },
                          currentApp ? 0 : 100,
                        )
                      },
                      disableScroll: true,
                    })),
                  ]}
                />
              )}
              {content.map(
                (section: {
                  title: string
                  icon?: ReactNode
                  content: ReactNode
                  collapsed?: boolean
                  titleBadge?: ReactNode
                }) => (
                  <Section
                    key={section.title}
                    title={section.title}
                    icon={section.icon}
                    sectionId={
                      section.title === 'Comparison Summary' ? 'summary' : toSlug(section.title)
                    }
                    titleBadge={
                      'titleBadge' in section ? (section.titleBadge as React.ReactNode) : undefined
                    }
                    collapsed={'collapsed' in section ? (section.collapsed as boolean) : undefined}
                    isParent={section.title.includes(`Editor's Verdict`) && 'children' in section}
                  >
                    {section.content as React.ReactNode}
                    {'children' in section &&
                      Array.isArray(section.children) &&
                      section.children.map(
                        (child: { title: string; content: React.ReactNode }, index: number) => (
                          <Fragment key={child.title || `child-${index}`}>{child.content}</Fragment>
                        ),
                      )}
                  </Section>
                ),
              )}
            </Container>

            <Container reduced={true}>
              {trendingComparisons.length > 0 && (
                <RelatedComparisons
                  title="Trending app comparisons"
                  buttons={[
                    {
                      label: 'See all',
                      link: '/compare',
                    },
                  ]}
                  comparisons={trendingComparisons}
                />
              )}

              <ExploreButton label="Explore more comparisons" link="/compare" />
            </Container>

            {/* Per-app review content for SEO — stored DB data not visible in comparison tab */}
            {!isDynamicComparison && (
              <div hidden aria-hidden="true">
                {apps.map((app) => {
                  const appData = appsWithReviewData.find((a) => a.id === app.id)
                  const review = getPrimaryReview(appData)
                  if (!review) return null

                  // At a Glance per category
                  const ratings = review?.reviewCategoryCriterionRating?.docs as
                    | ReviewCategoryCriterionRating[]
                    | undefined

                  const categorySections = categories.map((category) => {
                    const categoryCriteria = category.categoryCriteria?.docs as
                      | CategoryCriteriaCriterion[]
                      | undefined
                    if (!categoryCriteria?.length) return null

                    const criteriaWithRatings = categoryCriteria
                      .map((cc) => {
                        const criterion =
                          typeof cc.criterion === 'number' ? undefined : (cc.criterion as Criterion)
                        if (!criterion?.id) return null

                        const rating = getRatingForCriterion(app, criterion.id, category.id)
                        return { criterion, rating }
                      })
                      .filter(
                        (
                          entry,
                        ): entry is {
                          criterion: Criterion
                          rating: number | null
                        } => entry !== null,
                      )

                    if (criteriaWithRatings.length === 0) return null

                    const hasAnyScores = criteriaWithRatings.some(({ rating }) => rating !== null)
                    if (!hasAnyScores) return null

                    return (
                      <section key={category.id}>
                        <h2>
                          {hasMultipleCategories
                            ? `${app.name}: ${category.name}`
                            : `${app.name} At a Glance`}
                        </h2>
                        {generateIntro([app], category)}
                        <ul>
                          {criteriaWithRatings.map(({ criterion, rating }) => (
                            <li key={criterion.id}>
                              {criterion.name}
                              {rating !== null ? `: ${rating}/7` : ''}
                            </li>
                          ))}
                        </ul>
                      </section>
                    )
                  })

                  // Editor's Verdict per criterion (scoped to primary category)
                  const primaryCriteria = primaryCategory
                    ? criteria.filter((c) => categoryIdByCriterionId[c.id] === primaryCategory.id)
                    : criteria
                  const criterionEntries = primaryCriteria
                    .map((criterion) => {
                      const expectedCatId = categoryIdByCriterionId[criterion.id]
                      const matchingRating = ratings?.find((r) => {
                        const cc = r.categoryCriterion as CategoryCriteriaCriterion | undefined
                        const crit = cc?.criterion as Criterion | undefined
                        if (crit?.id !== criterion.id) return false
                        if (expectedCatId !== undefined) {
                          const catId =
                            typeof cc?.category === 'number'
                              ? cc.category
                              : (cc?.category as Category | undefined)?.id
                          if (catId !== undefined && catId !== expectedCatId) return false
                        }
                        return hasRichTextContent(r?.summary?.root?.children as Block[] | undefined)
                      })
                      const blocks = matchingRating?.summary?.root?.children as Block[] | undefined
                      if (!hasRichTextContent(blocks)) return null
                      return { criterion, blocks }
                    })
                    .filter(
                      (entry): entry is { criterion: Criterion; blocks: Block[] } => entry !== null,
                    )

                  // Editor's Verdict fallback: ACR summary or review summary
                  const acrs = appData?.categoryRecommendations?.docs as
                    | AppCategoryRecommendation[]
                    | undefined
                  const primaryAcr = acrs?.find((acr) => {
                    const cat = acr.category as Category
                    return cat.id === primaryCategory?.id
                  })
                  const acrSummaryBlocks = (primaryAcr?.summary as Record<string, unknown>)
                    ?.root as { children?: Block[] } | undefined
                  const reviewSummaryBlocks = (review?.summary as Record<string, unknown>)?.root as
                    | { children?: Block[] }
                    | undefined
                  const verdictFallbackBlocks =
                    acrSummaryBlocks?.children ?? reviewSummaryBlocks?.children

                  const hasVerdictContent =
                    criterionEntries.length > 0 ||
                    (verdictFallbackBlocks && verdictFallbackBlocks.length > 0)

                  if (!categorySections.some(Boolean) && !hasVerdictContent) return null

                  return (
                    <Fragment key={app.id}>
                      {categorySections}

                      {criterionEntries.length > 0 ? (
                        <section>
                          <h2>{`${app.name} Editor's Verdict`}</h2>
                          {criterionEntries.map(({ criterion, blocks }) => (
                            <section key={criterion.id}>
                              <h3>{criterion.name}</h3>
                              <RichText content={blocks} />
                            </section>
                          ))}
                        </section>
                      ) : verdictFallbackBlocks && verdictFallbackBlocks.length > 0 ? (
                        <section className={DEMOTED_HEADING_STYLES}>
                          <h2>{`${app.name} Editor's Verdict`}</h2>
                          <RichText content={demoteHeadings(verdictFallbackBlocks)} />
                        </section>
                      ) : null}
                    </Fragment>
                  )
                })}
              </div>
            )}
          </>
        )}
      </article>
    </>
  )
}
